import * as styles from '../FirstResponder.module.css';
import { renderMetric } from '../LivePreview/liveMetricDisplay';
import { LiveResultsSummary, LiveRunScope } from '../LivePreview/liveViewModel';

import { windowCaption } from './windowCaption';

/*
 * Canonical presentation component (GRAVY-10373 convergence, Phase 1 extraction).
 * Extracted verbatim from LiveConsole.tsx's Results <details> section.
 *
 * This heading is the FULL width of the pair windowCaption renders; the headline tiles above
 * carry the short one. It goes through the same function rather than reading windowLabel
 * directly, so the pair is visibly one source and stays one when either side changes.
 */

type Props = {
  results: LiveResultsSummary;
  // W5: the run mode these six numbers are scoped to; see runScopeFor in liveViewModel.
  runScope?: LiveRunScope;
};

const ResultsSection = ({ results, runScope }: Props) => (
  <details className={styles.collapse} data-testid="flp-results">
    <summary>
      Results{' '}
      <span className={`${styles.muted} ${styles.small}`}>
        ({windowCaption(results.windowLabel)}
        {runScope ? ` · ${runScope} runs` : ''})
      </span>
    </summary>
    <div
      className={styles.section}
      style={{ borderTop: '1px solid var(--n-lightest)' }}
    >
      <div className={styles.stats}>
        {(
          [
            ['Shifts acted on', results.shiftsActedOn],
            ['Filled by First Responder', results.filledByFirstResponder],
            ['Offers sent', results.offersSent],
            ['Accepted', results.accepted],
            ['No-shows / drops', results.noShowsOrDrops],
            ['Failed actions', results.failedActions],
          ] as const
        ).map(([label, value]) => (
          <div key={label} className={styles.stat}>
            {/* .statLabel is clamped to one line for the headline tiles' sake, and these labels
                share the class. The longest of them ("Filled by First Responder") can ellipsize
                in a narrow column, so it carries its own title for the same reason the tiles
                do — a clamp must never be the only copy of the text. */}
            <div className={styles.statLabel} title={label}>
              {label}
            </div>
            <div className={styles.statValue}>{renderMetric(value)}</div>
          </div>
        ))}
      </div>
      <p style={{ margin: '20px 0 0' }}>
        <strong>{results.headline}</strong>
      </p>
      <p
        className={`${styles.muted} ${styles.small}`}
        style={{ margin: '8px 0 0' }}
      >
        Treatment vs control analysis lives in Metabase
        {/* A null URL is a KNOWN ABSENCE — no dashboard has been published for this pilot yet —
            not a metric the backend could not substantiate. "Unknown" (renderMetric's rule) means
            the latter, and borrowing it here sent the operator hunting for a link that does not
            exist. Every metric above keeps Unknown for exactly the case Unknown is for. */}
        {results.metabaseUrl === null ? (
          <span data-testid="flp-results-metabase-unavailable">
            {' '}
            — No dashboard published for this pilot yet
          </span>
        ) : (
          <>
            {' — '}
            <a
              href={results.metabaseUrl}
              className={styles.btnPlain}
              data-testid="flp-results-metabase-link"
            >
              open the pilot dashboard
            </a>
          </>
        )}
        .
      </p>
    </div>
  </details>
);

export default ResultsSection;
